import type {
  AccommodationNode,
  AddStopInput,
  AppState,
  CreateTripInput,
  DayHealthStatus,
  DaySummary,
  FreeTimeBlock,
  IdeaItem,
  ItineraryStop,
  Profile,
  RouteLeg,
  OrderWarning,
  StopLocks,
  Trip,
  TripDay,
  TripSummary,
  TripSummaryConflict,
  UpdateStopInput,
} from "./types";

const DAY_MS = 86_400_000;
const MAX_TRIP_DAYS = 30;
const DEFAULT_LOCKS: StopLocks = { place: false, day: false, time: false, reservation: false };

export const emptyProfile: Profile = { name: "", homeCity: "", currency: "USD", pace: "balanced" };

export function createInitialState(): AppState {
  return {
    version: 2,
    onboardingComplete: false,
    profile: { ...emptyProfile },
    trips: [],
    activeTripId: null,
    screen: "trips",
    activity: [],
  };
}

export function makeId(prefix: string): string {
  const value = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `${prefix}_${value}`;
}

export function localDateString(date: Date): string {
  // Roam uses the user's planning locale (UTC+7 today) for date classification.
  return new Date(date.getTime() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export function isDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function isTimeString(value: string): boolean {
  return value === "" || /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

export function dateRange(startDate?: string, endDate?: string): string[] {
  if (!startDate || !endDate || !isDateString(startDate) || !isDateString(endDate)) return [];
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (start > end) return [];
  const days: string[] = [];
  for (let current = start; current <= end && days.length <= MAX_TRIP_DAYS; current += DAY_MS) {
    days.push(new Date(current).toISOString().slice(0, 10));
  }
  return days;
}

export function durationFromDates(startDate: string, endDate: string): number {
  const dates = dateRange(startDate, endDate);
  if (!dates.length) throw new Error("Start and end dates must form a valid range.");
  if (dates.length > MAX_TRIP_DAYS) throw new Error("Roam currently supports trips of up to 30 days.");
  return dates.length;
}

export function assertTripDates(startDate?: string, endDate?: string): void {
  if ((startDate === undefined) !== (endDate === undefined)) {
    throw new Error("Start and end dates must be provided together.");
  }
  if (startDate === undefined || endDate === undefined) return;
  if (!isDateString(startDate) || !isDateString(endDate)) {
    throw new Error("Start and end dates must use YYYY-MM-DD.");
  }
  if (startDate > endDate) throw new Error("The end date must be on or after the start date.");
  if (dateRange(startDate, endDate).length > MAX_TRIP_DAYS) {
    throw new Error("Roam currently supports trips of up to 30 days.");
  }
}

function assertDurationDays(value: number | undefined): number {
  if (!Number.isInteger(value) || value! < 1 || value! > MAX_TRIP_DAYS) {
    throw new Error("Duration must be an integer between 1 and 30 days.");
  }
  return value!;
}

function buildDays(durationDays: number, dates?: string[]): TripDay[] {
  return Array.from({ length: durationDays }, (_, index) => ({
    id: makeId("day"),
    dayIndex: index + 1,
    ...(dates?.[index] ? { date: dates[index] } : {}),
    locked: false,
  }));
}

export function createTrip(input: CreateTripInput, profile: Profile): Trip {
  const name = input.name.trim();
  const destination = input.destination.trim();
  if (!name) throw new Error("Trip name is required.");
  if (!destination) throw new Error("Destination is required.");

  assertTripDates(input.startDate, input.endDate);
  const dates = input.startDate && input.endDate ? dateRange(input.startDate, input.endDate) : undefined;
  const durationDays = dates ? durationFromDates(input.startDate!, input.endDate!) : assertDurationDays(input.durationDays);
  const now = new Date().toISOString();
  return {
    id: makeId("trip"),
    name,
    destination,
    durationDays,
    days: buildDays(durationDays, dates),
    stops: [],
    routeLegs: [],
    freeTimeBlocks: [],
    ideas: [],
    ...(dates ? { startDate: input.startDate, endDate: input.endDate } : {}),
    pace: input.pace ?? profile.pace,
    notes: input.notes?.trim() ?? "",
    createdAt: now,
    updatedAt: now,
  };
}

function dayForId(trip: Trip, dayId: string | null | undefined): TripDay | undefined {
  return dayId == null ? undefined : trip.days.find((day) => day.id === dayId);
}

function dayForDate(trip: Trip, date: string | undefined): TripDay | undefined {
  return date ? trip.days.find((day) => day.date === date) : undefined;
}

export function stopDayId(trip: Trip, stop: ItineraryStop): string | null {
  if (stop.dayId === null) return null;
  if (trip.days.some((day) => day.id === stop.dayId)) return stop.dayId;
  return dayForDate(trip, stop.date)?.id ?? null;
}

function dateForDay(trip: Trip, dayId: string | null): string | undefined {
  return dayId ? dayForId(trip, dayId)?.date : undefined;
}

function assertCoordinate(value: number, min: number, max: number, label: string): void {
  if (!Number.isFinite(value) || value < min || value > max) throw new Error(`${label} must be between ${min} and ${max}.`);
}

function assertStopFields(startTime: string, durationMinutes: number, cost: number): void {
  if (!isTimeString(startTime)) throw new Error("Start time must use 24-hour HH:MM format.");
  if (!Number.isInteger(durationMinutes) || durationMinutes < 5 || durationMinutes > 1440) {
    throw new Error("Duration must be an integer between 5 and 1440 minutes.");
  }
  if (!Number.isFinite(cost) || cost < 0 || cost > 1_000_000) {
    throw new Error("Estimated cost must be between 0 and 1,000,000.");
  }
}

function assertTravelBuffer(value: number | undefined): void {
  if (value === undefined) return;
  if (!Number.isInteger(value) || value < 1 || value > 1440) {
    throw new Error("Travel buffer must be an integer between 1 and 1440 minutes.");
  }
}

function groupKey(dayId: string | null): string { return dayId ?? "__unscheduled__"; }

function nextOrder(stops: ItineraryStop[], dayId: string | null): number {
  return stops.filter((stop) => stop.dayId === dayId).reduce((max, stop) => Math.max(max, stop.order), 0) + 1;
}

export function createStop(trip: Trip, input: AddStopInput): ItineraryStop {
  const name = input.name.trim();
  if (!name) throw new Error("Place name is required.");
  assertCoordinate(input.lat, -90, 90, "Latitude");
  assertCoordinate(input.lon, -180, 180, "Longitude");

  let dayId: string | null = input.dayId === undefined ? trip.days[0]?.id ?? null : input.dayId;
  if (input.date !== undefined) {
    const dateDay = dayForDate(trip, input.date);
    if (!dateDay) throw new Error(`Stop date must be between ${trip.startDate ?? "the trip dates"} and ${trip.endDate ?? "the trip dates"}.`);
    dayId = dateDay.id;
  }
  if (dayId !== null && !dayForId(trip, dayId)) throw new Error("Stop day does not belong to this trip.");
  if (input.order !== undefined && (!Number.isInteger(input.order) || input.order < 1 || input.order > nextOrder(trip.stops, dayId))) {
    throw new Error(`Order must be between 1 and ${nextOrder(trip.stops, dayId)}.`);
  }

  const startTime = input.startTime ?? "";
  const durationMinutes = input.durationMinutes ?? 60;
  const cost = input.cost ?? 0;
  assertStopFields(startTime, durationMinutes, cost);
  assertTravelBuffer(input.travelBufferMinutes);
  const now = new Date().toISOString();
  return {
    id: makeId("stop"),
    name,
    address: input.address?.trim() ?? "",
    lat: input.lat,
    lon: input.lon,
    dayId,
    order: input.order ?? nextOrder(trip.stops, dayId),
    date: dateForDay(trip, dayId),
    startTime,
    durationMinutes,
    notes: input.notes?.trim() ?? "",
    cost,
    transport: input.transport ?? "walk",
    ...(input.travelBufferMinutes === undefined ? {} : { travelBufferMinutes: input.travelBufferMinutes }),
    category: input.category?.trim() || "place",
    ...(input.placeRef ? { placeRef: input.placeRef } : {}),
    locks: { ...DEFAULT_LOCKS, ...(input.locks ?? {}) },
    ...(input.explanation?.trim() ? { explanation: input.explanation.trim() } : {}),
    createdAt: now,
    updatedAt: now,
  };
}

/** Insert a newly created stop and keep explicit order contiguous within its day. */
export function insertStop(stops: ItineraryStop[], stop: ItineraryStop): ItineraryStop[] {
  const group = sortStops(stops.filter((candidate) => candidate.dayId === stop.dayId));
  const position = Math.max(1, Math.min(stop.order, group.length + 1));
  group.splice(position - 1, 0, stop);
  const indexed = new Map(group.map((candidate, index) => [candidate.id, { ...candidate, order: index + 1 }]));
  return stops.map((candidate) => indexed.get(candidate.id) ?? candidate).concat(
    indexed.has(stop.id) && !stops.some((candidate) => candidate.id === stop.id) ? [indexed.get(stop.id)!] : [],
  );
}

export function patchStop(trip: Trip, stop: ItineraryStop, patch: UpdateStopInput): ItineraryStop {
  let dayId = stop.dayId;
  if (patch.date !== undefined) {
    const day = dayForDate(trip, patch.date);
    if (!day) throw new Error(`Stop date must be between ${trip.startDate ?? "the trip dates"} and ${trip.endDate ?? "the trip dates"}.`);
    dayId = day.id;
  }
  const next: ItineraryStop = {
    ...stop,
    dayId,
    date: dateForDay(trip, dayId),
    name: patch.name?.trim() || stop.name,
    address: patch.address === undefined ? stop.address : patch.address.trim(),
    startTime: patch.startTime ?? stop.startTime,
    durationMinutes: patch.durationMinutes ?? stop.durationMinutes,
    notes: patch.notes === undefined ? stop.notes : patch.notes.trim(),
    cost: patch.cost ?? stop.cost,
    transport: patch.transport ?? stop.transport,
    travelBufferMinutes: patch.transport && patch.transport !== "transit" && patch.transport !== "other"
      ? undefined
      : patch.travelBufferMinutes === undefined ? stop.travelBufferMinutes : patch.travelBufferMinutes,
    category: patch.category?.trim() || stop.category,
    placeRef: patch.placeRef === undefined ? stop.placeRef : patch.placeRef,
    locks: { ...stop.locks, ...(patch.locks ?? {}) },
    explanation: patch.explanation === undefined ? stop.explanation : patch.explanation.trim() || undefined,
    updatedAt: new Date().toISOString(),
  };
  assertStopFields(next.startTime, next.durationMinutes, next.cost);
  assertTravelBuffer(next.travelBufferMinutes);
  return next;
}

function compareStops(left: ItineraryStop, right: ItineraryStop): number {
  if (left.order !== right.order) return left.order - right.order;
  const leftTime = left.startTime || "99:99";
  const rightTime = right.startTime || "99:99";
  return leftTime.localeCompare(rightTime);
}

export function sortStops(stops: ItineraryStop[]): ItineraryStop[] {
  return stops.map((stop, index) => ({ stop, index })).sort((left, right) => compareStops(left.stop, right.stop) || left.index - right.index).map(({ stop }) => stop);
}

function reindexDay(stops: ItineraryStop[], dayId: string | null): ItineraryStop[] {
  return sortStops(stops.filter((stop) => stop.dayId === dayId)).map((stop, index) => ({ ...stop, order: index + 1 }));
}

export function moveStopToDay(stops: ItineraryStop[], stopId: string, targetDayId: string | null, targetPosition: number, dayDates?: Readonly<Record<string, string | undefined>>): ItineraryStop[] {
  const moved = stops.find((stop) => stop.id === stopId);
  if (!moved) throw new Error("Stop not found.");
  if (targetDayId !== null && !targetDayId.trim()) throw new Error("Target day is invalid.");
  const sourceDayId = moved.dayId;
  const without = stops.filter((stop) => stop.id !== stopId);
  const targetCount = without.filter((stop) => stop.dayId === targetDayId).length;
  if (!Number.isInteger(targetPosition) || targetPosition < 1 || targetPosition > targetCount + 1) {
    throw new Error(`Day position must be between 1 and ${targetCount + 1}.`);
  }
  const target = without.filter((stop) => stop.dayId === targetDayId);
  const movedStop = { ...moved, dayId: targetDayId, date: targetDayId && dayDates ? dayDates[targetDayId] : moved.date, updatedAt: new Date().toISOString() };
  target.splice(targetPosition - 1, 0, movedStop);
  if (sourceDayId === targetDayId) {
    const reordered = target.map((stop, index) => ({ ...stop, order: index + 1 }));
    let dayIndex = 0;
    return stops.map((stop) => stop.dayId === targetDayId ? reordered[dayIndex++] : stop);
  }
  const source = reindexDay(without, sourceDayId);
  const destination = target.map((stop, index) => ({ ...stop, order: index + 1 }));
  const replacements = new Map<string, ItineraryStop>([...source, ...destination].map((stop) => [stop.id, stop]));
  const emitted = new Set<string>();
  const result: ItineraryStop[] = [];
  for (const stop of stops) {
    const replacement = replacements.get(stop.id);
    if (replacement && !emitted.has(stop.id)) { result.push(replacement); emitted.add(stop.id); }
    else if (!replacement) result.push(stop);
  }
  for (const stop of [...source, ...destination]) if (!emitted.has(stop.id)) result.push(stop);
  return result;
}

/** Compatibility wrapper for callers that already know the stop's day. */
export function moveStopWithinDay(stops: ItineraryStop[], stopId: string, dayPosition: number): ItineraryStop[] {
  const moved = stops.find((stop) => stop.id === stopId);
  if (!moved) throw new Error("Stop not found.");
  return moveStopToDay(stops, stopId, moved.dayId, dayPosition);
}

/** Compatibility wrapper for the old whole-route position API. */
export function moveStop(stops: ItineraryStop[], stopId: string, position: number): ItineraryStop[] {
  const ordered = sortStops(stops);
  if (!Number.isInteger(position) || position < 1 || position > ordered.length) throw new Error(`Position must be between 1 and ${ordered.length}.`);
  const index = ordered.findIndex((stop) => stop.id === stopId);
  if (index < 0) throw new Error("Stop not found.");
  const [moved] = ordered.splice(index, 1);
  ordered.splice(position - 1, 0, moved);
  const byDay = new Map<string, ItineraryStop[]>();
  for (const stop of ordered) {
    const key = groupKey(stop.dayId);
    const group = byDay.get(key) ?? [];
    group.push(stop);
    byDay.set(key, group);
  }
  return [...byDay.values()].flat().map((stop, index) => {
    const dayGroup = byDay.get(groupKey(stop.dayId))!;
    return { ...stop, order: dayGroup.indexOf(stop) + 1, updatedAt: index === position - 1 ? new Date().toISOString() : stop.updatedAt };
  });
}

export function findOrderWarnings(stops: ItineraryStop[]): OrderWarning[] {
  const warnings: OrderWarning[] = [];
  const dayIds = [...new Set(stops.map((stop) => stop.dayId))];
  for (const dayId of dayIds) {
    const ordered = sortStops(stops.filter((stop) => stop.dayId === dayId));
    for (let index = 0; index < ordered.length - 1; index += 1) {
      const first = ordered[index];
      const second = ordered[index + 1];
      if (first.startTime && second.startTime && first.startTime > second.startTime) {
        warnings.push({ kind: "order_time_inversion", dayId, firstStopId: first.id, secondStopId: second.id, message: `${first.name} is ordered before ${second.name}, but starts later.` });
      }
    }
  }
  return warnings;
}

function timeToMinutes(value: string): number | null {
  if (!value) return null;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function formatMinutes(value: number): string { return `${Math.max(0, Math.round(value))} minutes`; }

export interface ScheduleConflict {
  firstStopId: string;
  secondStopId: string;
  dayId: string | null;
  date?: string;
  message: string;
  kind?: "schedule";
}

export function findScheduleConflicts(stops: ItineraryStop[]): ScheduleConflict[] {
  const conflicts: ScheduleConflict[] = [];
  for (const dayId of [...new Set(stops.map((stop) => stop.dayId))]) {
    const ordered = sortStops(stops.filter((stop) => stop.dayId === dayId));
    for (let index = 0; index < ordered.length - 1; index += 1) {
      const stop = ordered[index];
      const next = ordered[index + 1];
      const start = timeToMinutes(stop.startTime);
      const nextStart = timeToMinutes(next.startTime);
      if (start !== null && nextStart !== null && start + stop.durationMinutes > nextStart) {
        conflicts.push({ firstStopId: stop.id, secondStopId: next.id, dayId, date: stop.date, kind: "schedule", message: `${stop.name} overlaps ${next.name}${stop.date ? ` on ${stop.date}` : " on this day"}.` });
      }
    }
  }
  return conflicts;
}

function transportSpeed(mode: string): number {
  return mode === "walk" ? 4.5 : mode === "bike" ? 15 : mode === "drive" ? 35 : mode === "transit" ? 25 : 20;
}

type CoordinateNode = Pick<ItineraryStop, "id" | "lat" | "lon">;

export function routeFingerprint(
  from: CoordinateNode,
  to: CoordinateNode,
  transportMode: RouteLeg["transportMode"],
): string {
  const coordinate = (value: number) => value.toFixed(5);
  return [from.id, coordinate(from.lat), coordinate(from.lon), to.id, coordinate(to.lat), coordinate(to.lon), transportMode].join(":");
}

function haversineKm(left: CoordinateNode, right: CoordinateNode): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const deltaLat = toRadians(right.lat - left.lat);
  const deltaLon = toRadians(right.lon - left.lon);
  const lat1 = toRadians(left.lat);
  const lat2 = toRadians(right.lat);
  const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function buildEstimatedRouteLegs(trip: Trip, now = new Date()): RouteLeg[] {
  const buffer = trip.pace === "relaxed" ? 1.25 : trip.pace === "balanced" ? 1.1 : 1;
  const legs: RouteLeg[] = [];
  for (const day of trip.days) {
    const stops = sortStops(trip.stops.filter((stop) => stop.dayId === day.id && stop.dayId !== null));
    const accommodation = day.accommodation && Number.isFinite(day.accommodation.lat) && Number.isFinite(day.accommodation.lon)
      ? day.accommodation as CoordinateNode
      : null;
    const nodes: CoordinateNode[] = accommodation && stops.length ? [accommodation, ...stops, accommodation] : stops;
    for (let index = 0; index < nodes.length - 1; index += 1) {
      const from = nodes[index];
      const to = nodes[index + 1];
      const fromStop = stops.find((stop) => stop.id === from.id);
      const toStop = stops.find((stop) => stop.id === to.id);
      const transportMode = fromStop?.transport ?? "walk";
      const distanceKm = haversineKm(from, to);
      const planned = transportMode === "transit" || transportMode === "other";
      const travelBufferMinutes = planned ? fromStop?.travelBufferMinutes : undefined;
      legs.push({
        id: `leg_${from.id}_${to.id}`,
        dayId: day.id,
        fromStopId: from.id,
        toStopId: to.id,
        transportMode,
        distanceKm: Number(distanceKm.toFixed(2)),
        durationMinutes: planned ? travelBufferMinutes ?? 0 : Math.max(1, Math.ceil((distanceKm / transportSpeed(transportMode)) * 60 * buffer)),
        geometry: [[from.lon, from.lat], [to.lon, to.lat]],
        source: planned ? "planned" : "estimate",
        distanceMethod: "straight_line_estimate",
        durationMethod: planned ? "manual_buffer" : "speed_estimate",
        fingerprint: routeFingerprint(from, to, transportMode),
        ...(planned && travelBufferMinutes === undefined ? { fallbackReason: `${transportMode === "transit" ? "Transit" : "Travel"} time required.` } : {}),
        fetchedAt: now.toISOString(),
      });
    }
  }
  return legs;
}

function validRouteGeometry(value: RouteLeg["geometry"]): boolean {
  return Array.isArray(value) && value.length >= 2 && value.every((point) =>
    Array.isArray(point)
    && point.length === 2
    && Number.isFinite(point[0])
    && point[0] >= -180
    && point[0] <= 180
    && Number.isFinite(point[1])
    && point[1] >= -90
    && point[1] <= 90,
  );
}

export function effectiveRouteLegs(trip: Trip, now = new Date()): RouteLeg[] {
  const expected = buildEstimatedRouteLegs(trip, now);
  return expected.map((fallback) => {
    if (fallback.source === "planned") return fallback;
    const persisted = trip.routeLegs.find((leg) =>
      leg.id === fallback.id
      && leg.fingerprint === fallback.fingerprint
      && leg.dayId === fallback.dayId
      && leg.transportMode === fallback.transportMode
      && (leg.source === "provider" || leg.source === "cached")
      && Number.isFinite(leg.distanceKm)
      && leg.distanceKm >= 0
      && Number.isFinite(leg.durationMinutes)
      && leg.durationMinutes >= 0
      && validRouteGeometry(leg.geometry),
    );
    if (!persisted) return fallback;
    if (persisted.expiresAt && Date.parse(persisted.expiresAt) <= now.getTime()) {
      return { ...persisted, source: "cached", fallbackReason: persisted.fallbackReason ?? "Route data is older than 24 hours." };
    }
    return persisted;
  });
}

export function routeDistanceMethod(legs: RouteLeg[]): TripSummary["distanceMethod"] {
  if (!legs.length || legs.every((leg) => leg.distanceMethod === "straight_line_estimate")) return "straight_line_estimate";
  if (legs.every((leg) => leg.distanceMethod === "road_network")) return "road_network";
  return "mixed";
}

export function findTravelConflicts(trip: Trip): TripSummaryConflict[] {
  const conflicts: TripSummaryConflict[] = [];
  const legs = effectiveRouteLegs(trip);
  for (const leg of legs) {
    const from = trip.stops.find((stop) => stop.id === leg.fromStopId);
    const to = trip.stops.find((stop) => stop.id === leg.toStopId);
    if (!from || !to) continue;
    const fromStart = timeToMinutes(from.startTime);
    const toStart = timeToMinutes(to.startTime);
    if (fromStart === null || toStart === null) continue;
    const availableMinutes = toStart - (fromStart + from.durationMinutes);
    if (leg.durationMinutes > availableMinutes) {
      conflicts.push({ kind: "travel", firstStopId: from.id, secondStopId: to.id, dayId: leg.dayId, date: to.date, availableMinutes, requiredMinutes: leg.durationMinutes, message: `${from.name} to ${to.name} needs ${formatMinutes(leg.durationMinutes)} but only ${formatMinutes(availableMinutes)} is available.` });
    }
  }
  return conflicts;
}

function healthStatus(trip: Trip, activityMinutes: number, travelMinutes: number, conflicts: number): DayHealthStatus {
  if (conflicts > 0) return "impossible";
  const limits = trip.pace === "relaxed" ? { busy: 300, packed: 390, impossible: 480 } : trip.pace === "balanced" ? { busy: 420, packed: 540, impossible: 660 } : { busy: 540, packed: 660, impossible: 840 };
  const required = activityMinutes + travelMinutes;
  if (required > limits.impossible) return "impossible";
  if (required > limits.packed) return "too_packed";
  if (required > limits.busy) return "busy";
  return required > limits.busy * 0.55 ? "balanced" : "relaxed";
}

export function summarizeDay(trip: Trip, dayOrId: TripDay | string): DaySummary {
  const day = typeof dayOrId === "string" ? trip.days.find((candidate) => candidate.id === dayOrId) : dayOrId;
  if (!day) throw new Error("Trip day not found.");
  const stops = sortStops(trip.stops.filter((stop) => stop.dayId === day.id));
  const legs = effectiveRouteLegs(trip).filter((leg) => leg.dayId === day.id);
  const schedule = findScheduleConflicts(stops).map((conflict) => ({ ...conflict, kind: "schedule" as const }));
  const travel = findTravelConflicts({ ...trip, stops }).filter((conflict) => conflict.dayId === day.id);
  const schedulePairs = new Set(schedule.map((conflict) => `${conflict.firstStopId}:${conflict.secondStopId}`));
  const conflicts = [...schedule, ...travel.filter((conflict) => !schedulePairs.has(`${conflict.firstStopId}:${conflict.secondStopId}`))];
  const starts = stops.map((stop) => stop.startTime).filter(Boolean).sort();
  const ends = stops.map((stop) => { const start = timeToMinutes(stop.startTime); return start === null ? "" : `${String(Math.floor((start + stop.durationMinutes) / 60)).padStart(2, "0")}:${String((start + stop.durationMinutes) % 60).padStart(2, "0")}`; }).filter(Boolean).sort();
  const missingTravelBuffers: OrderWarning[] = legs.flatMap((leg) => {
    if (leg.source !== "planned" || leg.durationMinutes > 0) return [];
    const from = stops.find((stop) => stop.id === leg.fromStopId);
    const to = stops.find((stop) => stop.id === leg.toStopId);
    if (!from || !to) return [];
    return [{ kind: "transit_time_required" as const, dayId: day.id, firstStopId: from.id, secondStopId: to.id, message: `${leg.transportMode === "transit" ? "Transit" : "Travel"} time required from ${from.name} to ${to.name}.` }];
  });
  return { dayId: day.id, dayIndex: day.dayIndex, date: day.date, stopCount: stops.length, activityMinutes: stops.reduce((sum, stop) => sum + stop.durationMinutes, 0), travelMinutes: legs.reduce((sum, leg) => sum + leg.durationMinutes, 0), freeTimeCount: trip.freeTimeBlocks.filter((block) => block.dayId === day.id).length, distanceKm: Number(legs.reduce((sum, leg) => sum + leg.distanceKm, 0).toFixed(1)), conflictCount: conflicts.length, conflicts, warnings: [...findOrderWarnings(stops), ...missingTravelBuffers], earliestStartTime: starts[0] ?? null, latestEndTime: ends.at(-1) ?? null, status: healthStatus(trip, stops.reduce((sum, stop) => sum + stop.durationMinutes, 0), legs.reduce((sum, leg) => sum + leg.durationMinutes, 0), conflicts.length) };
}

export function summarizeTrip(trip: Trip, currency?: string): TripSummary {
  const days = trip.days.map((day) => summarizeDay(trip, day));
  const conflicts = days.flatMap((day) => day.conflicts);
  const warnings = days.flatMap((day) => day.warnings);
  const dateRangeValue = trip.startDate && trip.endDate ? `${trip.startDate} to ${trip.endDate}` : null;
  return { tripId: trip.id, name: trip.name, destination: trip.destination, dateRange: dateRangeValue, durationDays: trip.durationDays, stopCount: trip.stops.length, dayCount: trip.days.length, unscheduledStops: trip.stops.filter((stop) => stop.dayId === null).length, conflictCount: conflicts.length, conflicts, totalPlannedMinutes: trip.stops.reduce((sum, stop) => sum + stop.durationMinutes, 0), totalEstimatedCost: Number(trip.stops.reduce((sum, stop) => sum + stop.cost, 0).toFixed(2)), routeDistanceKm: Number(days.reduce((sum, day) => sum + day.distanceKm, 0).toFixed(1)), distanceMethod: routeDistanceMethod(effectiveRouteLegs(trip)), ...(currency ? { currency } : {}), days, warnings };
}

function cloneDay(day: TripDay, index: number, date?: string): TripDay { return { ...day, dayIndex: index + 1, ...(date ? { date } : { date: undefined }) }; }

export function updateTripRecord(trip: Trip, input: Partial<CreateTripInput>): Trip {
  const nextStart = input.startDate === undefined ? trip.startDate : input.startDate;
  const nextEnd = input.endDate === undefined ? trip.endDate : input.endDate;
  assertTripDates(nextStart, nextEnd);
  const dates = nextStart && nextEnd ? dateRange(nextStart, nextEnd) : undefined;
  const durationDays = dates ? durationFromDates(nextStart!, nextEnd!) : input.durationDays === undefined ? trip.durationDays : assertDurationDays(input.durationDays);
  const removedDayIds = new Set(trip.days.slice(durationDays).map((day) => day.id));
  if (removedDayIds.size && (trip.stops.some((stop) => stop.dayId !== null && removedDayIds.has(stop.dayId)) || trip.freeTimeBlocks.some((block) => block.dayId !== null && removedDayIds.has(block.dayId)))) {
    throw new Error("Move stops and free-time blocks off removed days before shortening this trip.");
  }
  const days = Array.from({ length: durationDays }, (_, index) => trip.days[index] ? cloneDay(trip.days[index], index, dates?.[index]) : { id: makeId("day"), dayIndex: index + 1, ...(dates?.[index] ? { date: dates[index] } : {}), locked: false });
  const nextStops = trip.stops.map((stop) => ({ ...stop, date: dateForDay({ ...trip, days }, stop.dayId), updatedAt: stop.updatedAt }));
  return { ...trip, name: input.name?.trim() || trip.name, destination: input.destination?.trim() || trip.destination, durationDays, days, stops: nextStops, ...(dates ? { startDate: nextStart, endDate: nextEnd } : { startDate: undefined, endDate: undefined }), pace: input.pace ?? trip.pace, notes: input.notes === undefined ? trip.notes : input.notes.trim(), updatedAt: new Date().toISOString() };
}

export function stopsOutsideDateRange(trip: Trip, startDate: string, endDate: string): ItineraryStop[] {
  assertTripDates(startDate, endDate);
  const allowed = new Set(dateRange(startDate, endDate));
  return trip.stops.filter((stop) => stop.dayId !== null && !allowed.has(dateForDay(trip, stop.dayId) ?? stop.date ?? ""));
}

function asRecord(value: unknown): Record<string, unknown> | null { return value && typeof value === "object" ? value as Record<string, unknown> : null; }
function stringValue(value: unknown, fallback = ""): string { return typeof value === "string" ? value : fallback; }
function numberValue(value: unknown, fallback: number): number { return typeof value === "number" && Number.isFinite(value) ? value : fallback; }

function matchingPersistedLeg(value: unknown, expected: RouteLeg): RouteLeg | null {
  const leg = asRecord(value);
  if (!leg
    || leg.dayId !== expected.dayId
    || leg.fromStopId !== expected.fromStopId
    || leg.toStopId !== expected.toStopId
    || leg.transportMode !== expected.transportMode
    || leg.fingerprint !== expected.fingerprint
    || typeof leg.distanceKm !== "number" || !Number.isFinite(leg.distanceKm) || leg.distanceKm < 0
    || typeof leg.durationMinutes !== "number" || !Number.isFinite(leg.durationMinutes) || leg.durationMinutes < 0
    || !Array.isArray(leg.geometry)
    || !leg.geometry.every((point) => Array.isArray(point) && point.length === 2 && point.every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate)))
    || (leg.source !== "provider" && leg.source !== "cached")
    || leg.distanceMethod !== "road_network"
    || leg.durationMethod !== "provider"
    || typeof leg.fetchedAt !== "string") return null;
  return {
    ...(leg as unknown as RouteLeg),
    id: expected.id,
    providerId: stringValue(leg.providerId, "openrouteservice"),
    ...(stringValue(leg.expiresAt) ? { expiresAt: stringValue(leg.expiresAt) } : {}),
    ...(stringValue(leg.fallbackReason) ? { fallbackReason: stringValue(leg.fallbackReason) } : {}),
  };
}

function migrateStop(raw: Record<string, unknown>, trip: Trip, index: number, stateVersion: number): ItineraryStop {
  const oldDate = stringValue(raw.date) || undefined;
  const persistedDayId = raw.dayId === null
    ? null
    : typeof raw.dayId === "string" && trip.days.some((day) => day.id === raw.dayId)
      ? raw.dayId
      : undefined;
  const dayId = stateVersion === 2 && persistedDayId !== undefined
    ? persistedDayId
    : dayForDate(trip, oldDate)?.id ?? null;
  const locks = asRecord(raw.locks);
  const travelBufferMinutes = Number.isInteger(raw.travelBufferMinutes) && Number(raw.travelBufferMinutes) >= 1 && Number(raw.travelBufferMinutes) <= 1440
    ? Number(raw.travelBufferMinutes)
    : undefined;
  return { id: stringValue(raw.id, makeId("stop")), name: stringValue(raw.name, "Unnamed place"), address: stringValue(raw.address), lat: numberValue(raw.lat, 0), lon: numberValue(raw.lon, 0), dayId, order: Number.isInteger(raw.order) ? Number(raw.order) : index + 1, date: dateForDay(trip, dayId), startTime: stringValue(raw.startTime), durationMinutes: numberValue(raw.durationMinutes, 60), notes: stringValue(raw.notes), cost: numberValue(raw.cost, 0), transport: raw.transport === "transit" || raw.transport === "drive" || raw.transport === "bike" || raw.transport === "other" ? raw.transport : "walk", ...(travelBufferMinutes === undefined ? {} : { travelBufferMinutes }), category: stringValue(raw.category, "place"), ...(asRecord(raw.placeRef) ? { placeRef: raw.placeRef as ItineraryStop["placeRef"] } : {}), locks: { place: locks?.place === true, day: locks?.day === true, time: locks?.time === true, reservation: locks?.reservation === true }, ...(stringValue(raw.explanation) ? { explanation: stringValue(raw.explanation) } : {}), createdAt: stringValue(raw.createdAt, new Date().toISOString()), updatedAt: stringValue(raw.updatedAt, new Date().toISOString()) };
}

function migrateTrip(raw: Record<string, unknown>, stateVersion: number): Trip {
  const startDate = stringValue(raw.startDate) || undefined;
  const endDate = stringValue(raw.endDate) || undefined;
  const dates = startDate && endDate ? dateRange(startDate, endDate) : undefined;
  const oldDays = Array.isArray(raw.days) ? raw.days : [];
  const durationDays = dates?.length ?? (numberValue(raw.durationDays, oldDays.length || 1));
  const days: TripDay[] = Array.from({ length: Math.min(MAX_TRIP_DAYS, Math.max(1, Math.floor(durationDays))) }, (_, index) => {
    const oldDay = asRecord(oldDays[index]);
    const oldDayDate = oldDay ? oldDay.date : undefined;
    const oldAccommodation = oldDay ? asRecord(oldDay.accommodation) : null;
    return { id: stringValue(oldDay?.id, `day_${stringValue(raw.id, "trip")}_${index + 1}`), dayIndex: index + 1, ...(dates?.[index] ? { date: dates[index] } : oldDayDate && isDateString(String(oldDayDate)) ? { date: String(oldDayDate) } : {}), locked: oldDay?.locked === true, ...(oldAccommodation ? { accommodation: oldAccommodation as unknown as AccommodationNode } : {}) };
  });
  const base: Trip = { id: stringValue(raw.id, makeId("trip")), name: stringValue(raw.name, "Untitled trip"), destination: stringValue(raw.destination), durationDays: days.length, days, stops: [], routeLegs: Array.isArray(raw.routeLegs) ? raw.routeLegs as RouteLeg[] : [], freeTimeBlocks: Array.isArray(raw.freeTimeBlocks) ? raw.freeTimeBlocks as FreeTimeBlock[] : [], ideas: Array.isArray(raw.ideas) ? raw.ideas as IdeaItem[] : [], ...(startDate && endDate ? { startDate, endDate } : {}), pace: raw.pace === "relaxed" || raw.pace === "packed" ? raw.pace : "balanced", notes: stringValue(raw.notes), createdAt: stringValue(raw.createdAt, new Date().toISOString()), updatedAt: stringValue(raw.updatedAt, new Date().toISOString()) };
  const oldStops = Array.isArray(raw.stops) ? raw.stops : [];
  base.stops = oldStops.map((stop, index) => migrateStop(asRecord(stop) ?? {}, base, index, stateVersion));
  // v1 stored one flat array. Reindex independently per day while preserving array order.
  if (stateVersion === 1) {
    const counters = new Map<string, number>();
    base.stops = base.stops.map((stop) => { const key = groupKey(stop.dayId); const order = (counters.get(key) ?? 0) + 1; counters.set(key, order); return { ...stop, order }; });
  }
  const persistedLegs = base.routeLegs;
  base.routeLegs = buildEstimatedRouteLegs(base).map((expected) => expected.source === "planned"
    ? expected
    : persistedLegs.map((leg) => matchingPersistedLeg(leg, expected)).find((leg): leg is RouteLeg => leg !== null) ?? expected,
  );
  void stateVersion;
  return base;
}

export function migrateV1State(value: unknown): AppState {
  const raw = asRecord(value);
  const fallback = createInitialState();
  if (!raw || !Array.isArray(raw.trips)) return fallback;
  return { ...fallback, onboardingComplete: raw.onboardingComplete === true, profile: { ...fallback.profile, ...(asRecord(raw.profile) ?? {}) } as Profile, trips: raw.trips.map((trip) => migrateTrip(asRecord(trip) ?? {}, 1)), activeTripId: typeof raw.activeTripId === "string" ? raw.activeTripId : null, screen: raw.screen === "planner" ? "planner" : "trips", activity: Array.isArray(raw.activity) ? raw.activity.slice(0, 40) as AppState["activity"] : [] };
}

export function normalizeState(value: unknown): AppState {
  const raw = asRecord(value);
  if (!raw || !Array.isArray(raw.trips)) return createInitialState();
  if (raw.version === 1) return migrateV1State(value);
  if (raw.version !== 2) return createInitialState();
  const fallback = createInitialState();
  return { ...fallback, onboardingComplete: raw.onboardingComplete === true, profile: { ...fallback.profile, ...(asRecord(raw.profile) ?? {}) } as Profile, trips: raw.trips.map((trip) => migrateTrip(asRecord(trip) ?? {}, 2)), activeTripId: typeof raw.activeTripId === "string" ? raw.activeTripId : null, screen: raw.screen === "planner" ? "planner" : "trips", activity: Array.isArray(raw.activity) ? raw.activity.slice(0, 40) as AppState["activity"] : [] };
}
